import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { Catalogue } from "@shared-core/data/catalogue-types";

export interface CatalogueCustomer {
  code: string;
  name: string;
}

/** URL state belongs to this tab and travels with item and return links. */
export function useCatalogueContext(
  defaultCustomer?: CatalogueCustomer | null,
) {
  const [params, setParams] = useSearchParams();
  const adapter = useDataAdapter();
  const code = params.has("customer")
    ? params.get("customer") || ""
    : (defaultCustomer?.code ?? "");
  const name =
    params.get("customerName") ||
    (defaultCustomer?.code === code ? defaultCustomer.name : code);
  const linkParams = new URLSearchParams(params);
  if (code) {
    linkParams.set("customer", code);
    linkParams.set("customerName", name);
  }
  const customer = code ? { code, name } : null;
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{
    code: string;
    catalogues: Catalogue[];
    failed: boolean;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    setResult(null);
    adapter.listCatalogues(code).then(
      (catalogues) => {
        if (!cancelled) setResult({ code, catalogues, failed: false });
      },
      () => {
        if (!cancelled) setResult({ code, catalogues: [], failed: true });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [adapter, code, retry]);
  const current = result?.code === code ? result : null;
  return {
    customer,
    catalogues: current?.catalogues ?? [],
    loading: !current,
    failed: current?.failed ?? false,
    retry: () => setRetry((n) => n + 1),
    search: linkParams.toString(),
    selectCustomer: (next: CatalogueCustomer | null) => {
      if ((next?.code ?? "") === code) return;
      setParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (next) {
            updated.set("customer", next.code);
            updated.set("customerName", next.name);
          } else {
            updated.set("customer", "");
            updated.delete("customerName");
          }
          // A product filter from the previous audience must not hide the new one.
          updated.delete("catalogues");
          updated.delete("scope");
          updated.delete("page");
          return updated;
        },
        { replace: true },
      );
    },
  };
}
